import { ADMIN_ROLES, type AdminAccountDto, type AdminRole } from '@bravo/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Card, ErrorBlock, Field, PageHeader, Select, Spinner, TextInput, statusTone } from '../components/ui';
import { apiData } from '../lib/api';
import { toastError, toastOk } from '../lib/feedback';
import { useFormat } from '../lib/format';

export function AdminsPage() {
  const { t } = useTranslation();
  const format = useFormat();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<AdminRole>('finance');
  const [tempPassword, setTempPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const query = useQuery({
    queryKey: ['admins'],
    queryFn: () => apiData<AdminAccountDto[]>('/admin/admins'),
  });

  async function create(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      const result = await apiData<{ admin: AdminAccountDto; temporaryPassword: string }>('/admin/admins', {
        method: 'POST',
        body: { email, name, role },
      });
      setTempPassword(result.temporaryPassword);
      setEmail('');
      setName('');
      toastOk(t('admins.created'));
      await queryClient.invalidateQueries({ queryKey: ['admins'] });
    } catch (error) {
      toastError(error, t('errorTitle'));
    } finally {
      setBusy(false);
    }
  }

  async function update(id: string, body: Record<string, unknown>) {
    try {
      const result = await apiData<{ admin?: AdminAccountDto; temporaryPassword?: string }>(
        body.resetPassword ? `/admin/admins/${id}/reset-password` : `/admin/admins/${id}`,
        { method: body.resetPassword ? 'POST' : 'PATCH', body: body.resetPassword ? {} : body },
      );
      if (result.temporaryPassword) setTempPassword(result.temporaryPassword);
      toastOk(t('saved'));
      await queryClient.invalidateQueries({ queryKey: ['admins'] });
    } catch (error) {
      toastError(error, t('errorTitle'));
    }
  }

  if (query.isLoading) return <Spinner />;
  if (query.isError || !query.data) {
    return <ErrorBlock message={t('errorTitle')} onRetry={() => void query.refetch()} />;
  }

  return (
    <div>
      <PageHeader title={t('admins.title')} subtitle={t('admins.subtitle')} />
      {tempPassword ? (
        <Card className="mb-4">
          <p className="text-sm">{t('admins.tempOnce')}</p>
          <p className="mt-2 font-mono text-lg" data-testid="temp-password">
            {tempPassword}
          </p>
        </Card>
      ) : null}
      <Card className="mb-6">
        <form className="grid gap-3 md:grid-cols-4" onSubmit={(event) => void create(event)}>
          <Field label={t('auth.email')}>
            <TextInput type="email" required value={email} onChange={(event) => setEmail(event.target.value)} />
          </Field>
          <Field label={t('admins.name')}>
            <TextInput required value={name} onChange={(event) => setName(event.target.value)} />
          </Field>
          <Field label={t('admins.role')}>
            <Select value={role} onChange={(event) => setRole(event.target.value as AdminRole)}>
              {ADMIN_ROLES.map((item) => (
                <option key={item} value={item}>
                  {format.role(item)}
                </option>
              ))}
            </Select>
          </Field>
          <div className="flex items-end">
            <Button type="submit" loading={busy}>
              {t('admins.create')}
            </Button>
          </div>
        </form>
      </Card>
      <ul className="divide-y divide-border rounded-2xl border border-border bg-card">
        {query.data.map((admin) => (
          <li key={admin.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-sm">
            <div>
              <p className="font-medium">{admin.name}</p>
              <p className="text-fg-muted">{admin.email}</p>
            </div>
            <Badge>{format.role(admin.role)}</Badge>
            <Badge tone={statusTone(admin.status)}>{format.status(admin.status)}</Badge>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="secondary"
                onClick={() =>
                  void update(admin.id, {
                    status: admin.status === 'active' ? 'disabled' : 'active',
                  })
                }
              >
                {admin.status === 'active' ? t('admins.disable') : t('admins.enable')}
              </Button>
              <Button variant="secondary" onClick={() => void update(admin.id, { resetTotp: true })}>
                {t('admins.resetTotp')}
              </Button>
              <Button variant="ghost" onClick={() => void update(admin.id, { resetPassword: true })}>
                {t('admins.resetPassword')}
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
